import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, LockKeyhole, TimerOff, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../lib/motion";
import { formatApiErrorDetail, useClientAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LENGTH = 8;

function Card({ children, testId }) {
  return (
    <div data-testid={testId} className="bg-white border-2 border-[#1A1A1A] rounded-3xl p-8 nb-shadow-lg">
      {children}
    </div>
  );
}

function InvalidLink() {
  return (
    <Card testId="reset-password-invalid">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#FF007F] text-white nb-shadow-sm">
        <TimerOff size={22} strokeWidth={2.5} />
      </span>
      <h1 className="mt-5 font-display font-black text-3xl md:text-4xl tracking-tighter">
        This link has expired.
      </h1>
      <p className="mt-3 text-sm font-semibold text-[#4A4A4A] leading-relaxed">
        Reset links work once and expire after 1 hour. Request a fresh one and we'll email it
        straight away.
      </p>
      <Link
        to="/forgot-password"
        data-testid="reset-password-request-new"
        className="mt-6 inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press"
      >
        Request a new link <ArrowUpRight size={14} strokeWidth={2.5} />
      </Link>
    </Card>
  );
}

function Success() {
  return (
    <Card testId="reset-password-success">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#00C2B8] nb-shadow-sm">
        <ShieldCheck size={22} strokeWidth={2.5} />
      </span>
      <h1 className="mt-5 font-display font-black text-3xl md:text-4xl tracking-tighter">
        Password updated.
      </h1>
      <p className="mt-3 text-sm font-semibold text-[#4A4A4A] leading-relaxed">
        You've been signed out everywhere. Taking you to the login page to sign in with your new
        password…
      </p>
      <Link
        to="/login"
        data-testid="reset-password-go-login"
        className="mt-6 inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press"
      >
        Go to login now <ArrowUpRight size={14} strokeWidth={2.5} />
      </Link>
    </Card>
  );
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useClientAuth();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(undefined); // undefined = checking
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValid(false);
      return;
    }
    axios
      .get(`${API}/auth/reset-password/validate`, { params: { token } })
      .then((r) => setValid(Boolean(r.data?.valid)))
      .catch(() => setValid(false));
  }, [token]);

  useEffect(() => {
    if (!done) return;
    const id = setTimeout(() => navigate("/login", { replace: true }), 2500);
    return () => clearTimeout(id);
  }, [done, navigate]);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= MIN_LENGTH && confirm === password && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      await logout(); // server already cleared cookies; drop any in-memory session too
      toast.success("Password updated — sign in with your new password.");
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setValid(false);
      } else {
        setError(formatApiErrorDetail(err?.response?.data?.detail));
      }
      setBusy(false);
    }
  };

  let content;
  if (done) {
    content = <Success />;
  } else if (valid === undefined) {
    content = (
      <div data-testid="reset-password-checking" className="flex justify-center py-10">
        <Loader2 size={28} className="animate-spin text-[#FF007F]" />
      </div>
    );
  } else if (!valid) {
    content = <InvalidLink />;
  } else {
    content = (
      <form onSubmit={submit} data-testid="reset-password-form" className="bg-white border-2 border-[#1A1A1A] rounded-3xl p-8 nb-shadow-lg">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#FFD700] nb-shadow-sm">
          <LockKeyhole size={22} strokeWidth={2.5} />
        </span>
        <h1 className="mt-5 font-display font-black text-3xl md:text-4xl tracking-tighter">
          Choose a new password.
        </h1>
        <p className="mt-2 text-sm font-semibold text-[#4A4A4A]">
          At least {MIN_LENGTH} characters. You'll be signed out on all other devices.
        </p>

        {error && (
          <p data-testid="reset-password-error" className="mt-4 bg-[#FF007F]/10 border-2 border-[#FF007F] text-[#1A1A1A] rounded-xl px-4 py-3 text-sm font-semibold">
            {error}
          </p>
        )}

        <label className="mt-5 block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">New password</span>
          <input
            type="password"
            required
            autoFocus
            minLength={MIN_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            data-testid="reset-password-input"
            className={`mt-1.5 w-full border-2 rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700] ${tooShort ? "border-[#FF007F]" : "border-[#1A1A1A]"}`}
          />
          {tooShort && (
            <span data-testid="reset-password-too-short" className="mt-1 block text-xs font-semibold text-[#FF007F]">
              Needs {MIN_LENGTH - password.length} more character{MIN_LENGTH - password.length === 1 ? "" : "s"}.
            </span>
          )}
        </label>
        <label className="mt-4 block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">Confirm password</span>
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type it again"
            data-testid="reset-password-confirm-input"
            className={`mt-1.5 w-full border-2 rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700] ${mismatch ? "border-[#FF007F]" : "border-[#1A1A1A]"}`}
          />
          {mismatch && (
            <span data-testid="reset-password-mismatch" className="mt-1 block text-xs font-semibold text-[#FF007F]">
              Passwords don't match yet.
            </span>
          )}
        </label>

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="reset-password-submit"
          className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press disabled:opacity-70"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} strokeWidth={2.5} />}
          Set new password
        </button>
      </form>
    );
  }

  return (
    <div data-testid="reset-password-page" className="min-h-[75vh] bg-[#F8F9FA] bg-grid flex items-center justify-center px-5 py-14">
      <Reveal className="w-full max-w-md">{content}</Reveal>
    </div>
  );
}
